/**
 * TCG PWA telemetry SDK.
 *
 * Streams fetch/image/Web Vitals/error events to the backend at /api/telemetry/*,
 * with a ring buffer, 5s/50-event flush cadence, and an IndexedDB offline queue
 * so cellular drops don't lose data. Also records rrweb session-replay chunks
 * and uploads them as multipart blobs.
 *
 * See docs/brainstorms/2026-04-18-tcg-show-debug-monitor-brainstorm.md for
 * the design context and the server-side schema at app/models/telemetry.py.
 */

import { openDB, type IDBPDatabase } from 'idb';
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import * as rrweb from 'rrweb';
import { getToken } from './storage';

// Exposed for Vite define() — injected at build time.
declare const __APP_VERSION__: string;

type LegacyEventKind =
  | 'fetch'
  | 'image_load'
  | 'route_change'
  | 'web_vital'
  | 'js_error'
  | 'unhandled_rejection'
  | 'console';

// Curated event names emitted by the beta-telemetry SDK. Mirror of the
// server's BETA_EVENT_NAMES allowlist in app/routers/telemetry.py — keep them
// in sync. A typo here is a TypeScript error; on the server it's a 400.
export const BETA_EVENT_NAMES = [
  'app_loaded',
  'auth_connected',
  'auth_failed',
  'online_changed',
  'search_performed',
  'search_failed',
  'scan_started',
  'scan_succeeded',
  'scan_failed',
  'scan_candidate_picked',
  'scan_cancelled',
  'scan_no_match',
  'scan_added_to_cart',
  'card_viewed',
  'condition_selected',
  'edition_toggled',
  'mode_changed',
  'buy_preset_used',
  'compare_added',
  'compare_opened',
  'api_error',
] as const;
export type BetaEventName = typeof BETA_EVENT_NAMES[number];

type EventKind = LegacyEventKind | BetaEventName;

interface TelemetryEvent {
  timestamp: string; // ISO
  kind: EventKind;
  url?: string;
  status_code?: number;
  duration_ms?: number;
  // Client-generated UUID for server-side dedup. Set by track() and
  // reportFetch; legacy push() callers leave it undefined and the server
  // generates one.
  id?: string;
  // X-Request-ID for FE↔BE correlation. Set by reportFetch and api_error
  // events; otherwise undefined.
  request_id?: string;
  payload?: Record<string, unknown>;
}

interface QueuedBatch {
  id: number;
  session_id: string;
  events: TelemetryEvent[];
  queued_at: number;
}

const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER = 50;
const MAX_BATCH_POST = 100; // matches backend schema limit
const RRWEB_CHUNK_MS = 10000;

const API_BASE = '/api/telemetry';
const OFFLINE_DB = 'tcg-telemetry-offline';
const OFFLINE_STORE = 'batches';

let sessionId: string | null = null;
let deviceId: string | null = null;
const buffer: TelemetryEvent[] = [];
let flushTimer: number | null = null;
let flushing = false;
let started = false;
let offlineDbPromise: Promise<IDBPDatabase> | null = null;

// -------- offline queue (IndexedDB) --------

async function getOfflineDb(): Promise<IDBPDatabase> {
  if (!offlineDbPromise) {
    offlineDbPromise = openDB(OFFLINE_DB, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(OFFLINE_STORE)) {
          db.createObjectStore(OFFLINE_STORE, { keyPath: 'id', autoIncrement: true });
        }
      },
    });
  }
  return offlineDbPromise;
}

async function persistBatch(batch: Omit<QueuedBatch, 'id'>): Promise<void> {
  try {
    const db = await getOfflineDb();
    await db.add(OFFLINE_STORE, batch as QueuedBatch);
  } catch (err) {
    // Last-resort: drop the batch to avoid infinite growth in pathological cases.
    console.warn('telemetry offline persist failed, dropping batch', err);
  }
}

async function drainOfflineQueue(): Promise<void> {
  let db: IDBPDatabase;
  try {
    db = await getOfflineDb();
  } catch {
    return;
  }

  const all = (await db.getAll(OFFLINE_STORE)) as QueuedBatch[];
  for (const entry of all) {
    const ok = await postBatch(entry.session_id, entry.events);
    if (ok) {
      await db.delete(OFFLINE_STORE, entry.id);
    } else {
      // stop — network is still bad; try again on next flush
      break;
    }
  }
}

// -------- HTTP --------

async function postBatch(sid: string, events: TelemetryEvent[]): Promise<boolean> {
  if (!events.length) return true;
  const token = await getToken();
  if (!token) return false;
  try {
    const res = await fetch(`${API_BASE}/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ session_id: sid, events: events.slice(0, MAX_BATCH_POST) }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function postRrweb(sid: string, sequence: number, blob: Blob): Promise<boolean> {
  const token = await getToken();
  if (!token) return false;
  try {
    const form = new FormData();
    form.append('session_id', sid);
    form.append('sequence', String(sequence));
    form.append('blob', blob, `${sequence}.bin`);
    const res = await fetch(`${API_BASE}/rrweb`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    return res.ok;
  } catch {
    return false;
  }
}

// -------- flush loop --------

async function flush(force = false): Promise<void> {
  if (flushing || !sessionId) return;
  if (!force && buffer.length === 0) return;
  flushing = true;
  try {
    const drained = buffer.splice(0, buffer.length);
    // Try online-first; fall back to IndexedDB if the network is flaky.
    const ok = await postBatch(sessionId, drained);
    if (!ok) {
      await persistBatch({ session_id: sessionId, events: drained, queued_at: Date.now() });
    } else {
      // Try to send any previously-queued batches now that the network is up.
      await drainOfflineQueue();
    }
  } finally {
    flushing = false;
  }
}

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = window.setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);
}

// -------- public API --------

function push(event: Omit<TelemetryEvent, 'timestamp'> & { timestamp?: string }) {
  // Always buffer — flush() returns early if sessionId isn't set yet, and
  // initTelemetry() drains the pending buffer once the session opens. Without
  // this, events fired between module-load and initTelemetry's POST returning
  // (e.g. App.tsx's app_loaded useEffect) get silently dropped because that
  // race is consistently lost on most networks. Bounded to 2× MAX_BUFFER as
  // a safety net for the case where init never succeeds (e.g. logged-out
  // boot that never logs in) — drops the oldest event when over the cap.
  const full: TelemetryEvent = {
    timestamp: event.timestamp ?? new Date().toISOString(),
    ...event,
  };
  buffer.push(full);
  if (buffer.length > MAX_BUFFER * 2) {
    buffer.shift();
  }
  if (started && sessionId && buffer.length >= MAX_BUFFER) {
    void flush();
  }
}

export function reportFetch(args: {
  url: string;
  status: number;
  duration_ms: number;
  method?: string;
  request_size?: number;
  response_size?: number;
  request_id?: string;
}) {
  // A fingerprint for the "wrong URL built" bug class: normalized path + sorted param keys.
  // Values aren't included because they change per request, but the *shape* of the call should
  // be stable. If a code change suddenly starts emitting a new fingerprint for a known path,
  // the agent can flag it.
  let fingerprint = args.url;
  try {
    const u = new URL(args.url, window.location.origin);
    const params = [...u.searchParams.keys()].sort().join(',');
    fingerprint = `${u.pathname}?${params}`;
  } catch {
    /* ignore URL parse failures */
  }
  push({
    kind: 'fetch',
    url: args.url,
    status_code: args.status,
    duration_ms: args.duration_ms,
    request_id: args.request_id,
    payload: {
      method: args.method,
      request_size: args.request_size,
      response_size: args.response_size,
      fingerprint,
    },
  });
}

/**
 * Emit a curated beta-telemetry event.
 *
 * Use this for the beta-tester event allowlist (see BETA_EVENT_NAMES). The
 * server validates `name` against the same set; an unknown name fails the
 * batch with 400 instead of being silently accepted as a typo.
 *
 * Each call generates a fresh UUID `id` so a flaky-network retry of the
 * flush is dedup'd via ON CONFLICT (id) DO NOTHING server-side. Pass
 * `requestId` from the api.ts fetch wrapper when the event is associated
 * with a specific request (e.g. an api_error fired alongside a non-2xx).
 */
export function track(
  name: BetaEventName,
  props?: Record<string, unknown>,
  requestId?: string,
) {
  push({
    kind: name,
    request_id: requestId,
    id: crypto.randomUUID(),
    payload: props,
  });
}

export function reportImageLoad(url: string, durationMs: number, meta?: Record<string, unknown>) {
  push({ kind: 'image_load', url, duration_ms: Math.round(durationMs), payload: meta });
}

export function reportImageError(url: string, meta?: Record<string, unknown>) {
  push({ kind: 'image_load', url, payload: { error: true, ...(meta ?? {}) } });
}

export function reportRouteChange(from: string, to: string, durationMs?: number) {
  push({
    kind: 'route_change',
    url: to,
    duration_ms: durationMs !== undefined ? Math.round(durationMs) : undefined,
    payload: { from, to },
  });
}

export function reportJsError(err: Error | string, extra?: Record<string, unknown>) {
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error ? err.stack : undefined;
  push({
    kind: 'js_error',
    payload: { message, stack, ...(extra ?? {}) },
  });
}

// -------- session lifecycle --------

function computeDeviceId(): string {
  const key = 'tcg-telemetry-device-id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

async function openSession(): Promise<string | null> {
  const token = await getToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        device_id: deviceId,
        user_agent: navigator.userAgent,
        viewport_w: window.innerWidth,
        viewport_h: window.innerHeight,
        network_type:
          (navigator as unknown as { connection?: { effectiveType?: string } }).connection
            ?.effectiveType ?? undefined,
        app_version: __APP_VERSION__,
      }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { session_id: string };
    return body.session_id;
  } catch {
    return null;
  }
}

async function closeSession(): Promise<void> {
  if (!sessionId) return;
  const token = await getToken();
  if (!token) return;
  // Use sendBeacon when available for reliable tab-close delivery.
  const url = `${API_BASE}/session/${sessionId}/close`;
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url);
    } else {
      await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      });
    }
  } catch {
    /* best-effort only */
  }
}

// -------- rrweb session replay --------

let rrwebEvents: unknown[] = [];
let rrwebSequence = 0;
let rrwebStop: (() => void) | null = null;

function startRrweb() {
  if (rrwebStop) return;
  try {
    rrwebStop = rrweb.record({
      emit(event) {
        rrwebEvents.push(event);
      },
      // Mask password inputs + anything tagged with data-no-record.
      maskAllInputs: false,
      maskInputOptions: { password: true },
      blockClass: 'data-no-record',
    }) as (() => void) | null;
  } catch (err) {
    console.warn('rrweb.record failed, continuing without session replay', err);
    return;
  }

  window.setInterval(() => {
    void flushRrwebChunk();
  }, RRWEB_CHUNK_MS);
}

async function flushRrwebChunk() {
  if (!sessionId || rrwebEvents.length === 0) return;
  const drained = rrwebEvents;
  rrwebEvents = [];
  rrwebSequence += 1;
  const seq = rrwebSequence;
  const blob = new Blob([JSON.stringify(drained)], { type: 'application/json' });
  const ok = await postRrweb(sessionId, seq, blob);
  if (!ok) {
    // rrweb chunks are large and ephemeral — if the network's bad, just drop
    // this chunk rather than blowing up IndexedDB storage at a card show.
  }
}

// -------- Web Vitals --------

function initWebVitals() {
  const emit = (metric: { name: string; value: number; rating?: string; id?: string }) => {
    push({
      kind: 'web_vital',
      duration_ms: Math.round(metric.value),
      payload: {
        metric: metric.name,
        rating: metric.rating,
        id: metric.id,
      },
    });
  };
  onCLS(emit);
  onFCP(emit);
  onINP(emit);
  onLCP(emit);
  onTTFB(emit);
}

// -------- global error handlers --------

function installGlobalErrorHandlers() {
  window.addEventListener('error', (evt) => {
    push({
      kind: 'js_error',
      url: evt.filename,
      payload: {
        message: evt.message,
        stack: evt.error && evt.error.stack ? evt.error.stack : undefined,
        line: evt.lineno,
        col: evt.colno,
      },
    });
  });
  window.addEventListener('unhandledrejection', (evt) => {
    const reason = evt.reason;
    const message =
      reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    push({
      kind: 'unhandled_rejection',
      payload: { message, stack },
    });
  });
}

// -------- init --------

export async function initTelemetry(opts?: { enableRrweb?: boolean }): Promise<void> {
  if (started) return;
  deviceId = computeDeviceId();

  const sid = await openSession();
  if (!sid) {
    // Not authenticated or network down at boot. The PWA will call initTelemetry
    // again after a successful login (useAuth.login wires this up).
    return;
  }
  sessionId = sid;
  started = true;

  installGlobalErrorHandlers();
  initWebVitals();
  scheduleFlush();

  if (opts?.enableRrweb !== false) {
    startRrweb();
  }

  // Drain any events pushed before initTelemetry's POST returned (e.g.
  // App.tsx's app_loaded useEffect, which runs on first React paint —
  // typically before our /session POST completes). Without this kick the
  // events sit in the buffer until the next 5s flush tick.
  if (buffer.length > 0) {
    void flush();
  }

  // One-shot kick to drain anything stuck from a previous run.
  void drainOfflineQueue();

  // Tab close: flush + close session on best effort.
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flush(true);
    }
  });
  window.addEventListener('beforeunload', () => {
    void flush(true);
    void closeSession();
  });
}

export function isTelemetryActive(): boolean {
  return started;
}

export function getSessionId(): string | null {
  return sessionId;
}
